/**
 * S8 access administration — the shell's half (seams S8/S9, UX F4).
 *
 * Two of these functions exist to make a failure *unreachable* rather than
 * merely unlikely:
 *
 * * `moverChanges` returns the grant and the revoke as ONE payload, so a mover
 *   cannot keep old rights because someone forgot the second step. The API
 *   applies them in one transaction; this makes the UI unable to send half.
 * * `isSelfDemotion` makes an owner state the consequence out loud before they
 *   remove their own edit rights — the one change nobody can undo for
 *   themselves afterwards.
 */

import { API_BASE } from "./api";

export type Level = "view" | "edit";

export interface AccessArea {
  area_key: string;
  display_name: string;
  rail_order: number;
}

export interface Entitlement {
  person_email: string;
  area_key: string;
  level: Level;
}

/**
 * A MODULE level (D-6 v0.6). `none` is an explicit CLOSE, not an absence —
 * the absence is having no row at all, which inherits the area. The two are
 * one word apart and opposite, so they are never both spelled as "no".
 */
export type ModuleLevel = Level | "none";

/** A tab a grant can name, as the registry declares it. */
export interface TabCatalogueEntry {
  tab_key: string;
  display_name: string;
}

/** A module someone could be given a level on. The inventory, not a grant. */
export interface ModuleCatalogueEntry {
  area_key: string;
  module_key: string;
  display_name: string;
  /** Absent on an API older than the tab grant; the screen then shows no tab cells. */
  tabs?: TabCatalogueEntry[];
}

export interface ModuleEntitlement {
  person_email: string;
  module_key: string;
  level: ModuleLevel;
}

/** A pending MODULE change. `level: null` deletes the row (back to inherit). */
export interface ModuleChange {
  person_email: string;
  module_key: string;
  level: ModuleLevel | null;
}

export interface ModuleAuditEntry {
  actor_email: string;
  action: "grant" | "change" | "revoke";
  person_email: string;
  module_key: string;
  level: ModuleLevel | null;
  changed_at: string;
}

/**
 * One module as the admin screen shows it: what it resolves to, and whether a
 * module row or the area decided that.
 *
 * `source` is the whole point of the row. "Ioan has view on Profit Yield" is
 * not actionable — "he has view BECAUSE the area says so" and "he has view
 * because someone set this module to view" are fixed in different places, and
 * an admin screen that shows the result without the cause makes the reader
 * guess which control to touch.
 */
export interface ModuleCell {
  area_key: string;
  module_key: string;
  display_name: string;
  /** What the person can actually do here. `none` = no access. */
  effective: ModuleLevel;
  /** `area` when inherited, `module` when a module row decided it. */
  source: "area" | "module";
  /** The module row's own level, if one exists. */
  override: ModuleLevel | null;
}

/** A TAB grant (spec tab-entitlements.md): narrower than a module, decides its tab outright. */
export interface TabEntitlement {
  person_email: string;
  module_key: string;
  tab_key: string;
  level: ModuleLevel;
}

/** A pending TAB change. `level: null` deletes the row (back to the module's level). */
export interface TabChange {
  person_email: string;
  module_key: string;
  tab_key: string;
  level: ModuleLevel | null;
}

export interface TabAuditEntry {
  actor_email: string;
  action: "grant" | "change" | "revoke";
  person_email: string;
  module_key: string;
  tab_key: string;
  level: ModuleLevel | null;
  changed_at: string;
}

/**
 * One tab as the admin screen shows it. `source` names the deciding grant:
 * the tab's own row, its module's row, or the area — three different controls.
 */
export interface TabCell {
  module_key: string;
  tab_key: string;
  display_name: string;
  /** What the person can actually do on this tab. `none` = no access. */
  effective: ModuleLevel;
  source: "area" | "module" | "tab";
  /** The tab row's own level, if one exists. */
  override: ModuleLevel | null;
}

/** A pending change. `level: null` is a revocation. */
export interface Change {
  person_email: string;
  area_key: string;
  level: Level | null;
}

export interface AuditEntry {
  actor_email: string;
  action: "grant" | "change" | "revoke";
  person_email: string;
  area_key: string;
  level: Level | null;
  changed_at: string;
}

/** Someone on the roster. They may hold no entitlements at all. */
export interface Person {
  email: string;
  display_name: string;
}

export interface GridRow {
  person_email: string;
  areaKeys: string[];
  cells: Record<string, Level | "none">;
}

/** The one domain the portal onboards — the same rule the API enforces. */
export const PORTAL_DOMAIN = "ketosource.co";

export type NewPersonProblem = "ok" | "empty" | "not-ketosource" | "duplicate";

/**
 * Is this address addable? Named problems, never a boolean.
 *
 * The caller renders a *reason*: "that's not a ketosource.co address" and
 * "that person is already listed" are different mistakes with different fixes,
 * and a single "invalid" leaves the person to guess which one they made.
 */
export function validateNewPersonEmail(email: string, existing: string[]): NewPersonProblem {
  const candidate = email.trim().toLowerCase();
  if (!candidate) return "empty";
  if (!/^[^\s@]+@[^\s@]+$/.test(candidate)) return "not-ketosource";
  if (!candidate.endsWith(`@${PORTAL_DOMAIN}`)) return "not-ketosource";
  if (existing.some((e) => e.trim().toLowerCase() === candidate)) return "duplicate";
  return "ok";
}

/** A first name from a Ketosource address. Transforms data we have; invents nothing. */
function nameFor(email: string): string {
  const local = email.split("@")[0] ?? email;
  const first = local.split(/[._-]/)[0] ?? local;
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : email;
}

/**
 * People × areas, with **every** cell stated.
 *
 * "No entitlement" renders as `none`, never as a blank the reader has to
 * interpret. The whole value of the grid is a spot-the-anomaly scan (UX W4),
 * and a scan over blanks is a scan over ambiguity.
 */
export function buildGrid(
  areas: AccessArea[],
  entitlements: Entitlement[],
  roster: Person[] = [],
): GridRow[] {
  const areaKeys = [...areas].sort((a, b) => a.rail_order - b.rail_order).map((a) => a.area_key);
  // The ROSTER is the people source, unioned with anyone holding a row.
  //
  // Deriving people from entitlements alone was a closed loop: someone with
  // nothing had no row, so there was nowhere to give them their first area and
  // the screen could only ever adjust access someone already had. The union
  // keeps a stray entitlement whose person row is missing visible rather than
  // hiding an access nobody can then see to remove.
  const people = [
    ...new Set([
      ...roster.map((p) => p.email.toLowerCase()),
      ...entitlements.map((e) => e.person_email.toLowerCase()),
    ]),
  ].sort();

  return people.map((person) => {
    const cells: Record<string, Level | "none"> = {};
    for (const key of areaKeys) {
      const held = entitlements.find(
        (e) => e.person_email.toLowerCase() === person && e.area_key === key,
      );
      cells[key] = held ? held.level : "none";
    }
    return { person_email: person, areaKeys, cells };
  });
}

/**
 * Every module in the areas this person can be given, with what they resolve
 * to today (D-6 v0.6).
 *
 * Modules are listed for EVERY area, including ones where the person holds
 * nothing — that is how a module-only grant gets made in the first place
 * ("give Fiona Canvas, but not the rest of Core Tools"). Hiding an area they
 * have no grant in would hide the exact case the narrower grant exists for.
 */
export function buildModuleCells(
  personEmail: string,
  areas: AccessArea[],
  catalogue: ModuleCatalogueEntry[],
  moduleEntitlements: ModuleEntitlement[],
  entitlements: Entitlement[],
): ModuleCell[] {
  const person = personEmail.toLowerCase();
  const order = new Map(areas.map((a) => [a.area_key, a.rail_order]));
  const areaLevel = (areaKey: string): Level | null =>
    entitlements.find((e) => e.person_email.toLowerCase() === person && e.area_key === areaKey)
      ?.level ?? null;

  return [...catalogue]
    .sort(
      (a, b) =>
        (order.get(a.area_key) ?? 0) - (order.get(b.area_key) ?? 0) ||
        a.display_name.localeCompare(b.display_name),
    )
    .map((entry) => {
      const override =
        moduleEntitlements.find(
          (m) => m.person_email.toLowerCase() === person && m.module_key === entry.module_key,
        )?.level ?? null;
      const inherited = areaLevel(entry.area_key);
      return {
        area_key: entry.area_key,
        module_key: entry.module_key,
        display_name: entry.display_name,
        // The same rule the resolver applies, stated once more on the client
        // so the screen can never show a level the API would not honour.
        effective: override ?? inherited ?? "none",
        source: override === null ? ("area" as const) : ("module" as const),
        override,
      };
    });
}

/**
 * The next state when an admin clicks a module cell.
 *
 * Four states, in the order an admin actually moves through them: inherit the
 * area → view → edit → closed → back to inherit. `null` means "inherit",
 * which is a REVOKE of the module row, not a close — see `ModuleChange`.
 */
export function nextModuleLevel(current: ModuleLevel | null): ModuleLevel | null {
  if (current === null) return "view";
  if (current === "view") return "edit";
  if (current === "edit") return "none";
  return null;
}

/**
 * What a module cell says, in words a reader does not have to decode.
 *
 * An inherited cell names the area as the reason, because the fix for it is in
 * a different control than the fix for an explicit one.
 */
export function describeModuleCell(cell: ModuleCell, areas: AccessArea[]): string {
  const areaName =
    areas.find((a) => a.area_key === cell.area_key)?.display_name ?? cell.area_key;
  if (cell.source === "area") {
    return cell.effective === "none"
      ? `No access — nothing granted in ${areaName}`
      : `${cell.effective} — inherited from ${areaName}`;
  }
  return cell.effective === "none"
    ? `Closed for this module only — ${areaName} access does not reach it`
    : `${cell.effective} — set on this module, overriding ${areaName}`;
}

/**
 * The tab cells under every module that declares tabs (spec tab-entitlements.md S5).
 *
 * Precedence is the resolver's, stated once more on the client: a tab row
 * decides its tab, else the module row decides, else the area. Modules with
 * no declared tabs contribute nothing.
 */
export function buildTabCells(
  personEmail: string,
  catalogue: ModuleCatalogueEntry[],
  moduleEntitlements: ModuleEntitlement[],
  tabEntitlements: TabEntitlement[],
  entitlements: Entitlement[],
  areas: AccessArea[],
): TabCell[] {
  const person = personEmail.toLowerCase();
  const order = new Map(areas.map((a) => [a.area_key, a.rail_order]));
  const areaLevel = (areaKey: string): Level | null =>
    entitlements.find((e) => e.person_email.toLowerCase() === person && e.area_key === areaKey)
      ?.level ?? null;

  const cells: TabCell[] = [];
  const modules = [...catalogue].sort(
    (a, b) =>
      (order.get(a.area_key) ?? 0) - (order.get(b.area_key) ?? 0) ||
      a.display_name.localeCompare(b.display_name),
  );
  for (const entry of modules) {
    const moduleOverride =
      moduleEntitlements.find(
        (m) => m.person_email.toLowerCase() === person && m.module_key === entry.module_key,
      )?.level ?? null;
    const moduleLevel: ModuleLevel = moduleOverride ?? areaLevel(entry.area_key) ?? "none";
    for (const tab of entry.tabs ?? []) {
      const override =
        tabEntitlements.find(
          (t) =>
            t.person_email.toLowerCase() === person &&
            t.module_key === entry.module_key &&
            t.tab_key === tab.tab_key,
        )?.level ?? null;
      cells.push({
        module_key: entry.module_key,
        tab_key: tab.tab_key,
        display_name: tab.display_name,
        effective: override ?? moduleLevel,
        source: override !== null ? "tab" : moduleOverride !== null ? "module" : "area",
        override,
      });
    }
  }
  return cells;
}

/** Same cycle as modules: inherit -> view -> edit -> closed -> inherit. `null` is inherit. */
export function nextTabLevel(current: ModuleLevel | null): ModuleLevel | null {
  return nextModuleLevel(current);
}

/** What a tab cell says: "inherited", "set here" or "closed". */
export function describeTabCell(cell: TabCell): string {
  if (cell.source === "tab") {
    return cell.effective === "none"
      ? "closed — this tab is shut for this person"
      : `${cell.effective} — set here, overriding its module`;
  }
  const from = cell.source === "module" ? "its module" : "its area";
  return cell.effective === "none"
    ? `no access — inherited from ${from}`
    : `${cell.effective} — inherited from ${from}`;
}

/**
 * The changes a move requires — as one payload (seam S9, UX F4 alternate).
 *
 * The revoke is FIRST. Order does not change the outcome inside a transaction,
 * but it makes the intent readable in the request body and in the audit trail:
 * this person left one area and joined another, not "was granted something and
 * separately lost something".
 */
export function moverChanges(
  personEmail: string,
  fromAreaKey: string | null,
  toAreaKey: string | null,
  level: Level,
): Change[] {
  const changes: Change[] = [];
  if (fromAreaKey) changes.push({ person_email: personEmail, area_key: fromAreaKey, level: null });
  if (toAreaKey) changes.push({ person_email: personEmail, area_key: toAreaKey, level });
  return changes;
}

/**
 * Is this change the actor reducing their OWN access? (UX S8 edge.)
 *
 * A confirmation naming the consequence — "You'll lose edit rights in Margin
 * Engine" — is the difference between a decision and an accident, and this is
 * the one change the person cannot reverse for themselves afterwards.
 */
export function isSelfDemotion(
  actorEmail: string,
  current: Entitlement[],
  change: Change,
): boolean {
  if (change.person_email.toLowerCase() !== actorEmail.toLowerCase()) return false;
  const held = current.find(
    (e) =>
      e.person_email.toLowerCase() === change.person_email.toLowerCase() &&
      e.area_key === change.area_key,
  );
  if (!held) return false;
  if (change.level === null) return true;
  return held.level === "edit" && change.level === "view";
}

/**
 * One line of the change log: who granted what to whom, and when.
 *
 * Uses the area's DISPLAY name, never its key — a log line reading
 * "margin_engine" would be a second vocabulary for an area the rail already
 * names (I-5). An area the naming source no longer lists falls back to the key
 * rather than dropping the line, because a history entry we cannot fully label
 * is still a history entry.
 */
export function describeAudit(entry: AuditEntry, areas: AccessArea[]): string {
  const areaName =
    areas.find((a) => a.area_key === entry.area_key)?.display_name ?? entry.area_key;
  const date = entry.changed_at.slice(0, 10);
  const verb = { grant: "granted", change: "changed", revoke: "revoked" }[entry.action];
  const level = entry.action === "revoke" || !entry.level ? "" : ` ${entry.level}`;
  return `${nameFor(entry.actor_email)} ${verb} ${nameFor(entry.person_email)}${level} in ${areaName} · ${date}`;
}

export interface AccessGridData {
  areas: AccessArea[];
  entitlements: Entitlement[];
  /** Absent on an API older than KET-3491; the grid falls back to entitlements. */
  people?: Person[];
  recent: AuditEntry[];
  /**
   * The module inventory and the module-level grants (D-6 v0.6). Both optional:
   * an API deployed before this change sends neither, and the screen then
   * renders exactly as it did — areas only — rather than breaking on a field
   * it expected. A portal whose UI and API deploy separately has to survive
   * the gap in between.
   */
  modules?: ModuleCatalogueEntry[];
  module_entitlements?: ModuleEntitlement[];
  module_recent?: ModuleAuditEntry[];
  /** Tab grants (spec tab-entitlements.md). Absent on an older API, same reasoning. */
  tab_entitlements?: TabEntitlement[];
  tab_recent?: TabAuditEntry[];
}

/** Read the grid. Throws on refusal so the caller can render S8's error state. */
export async function fetchAccess(signal?: AbortSignal): Promise<AccessGridData> {
  const res = await fetch(`${API_BASE}/v1/entitlements`, { credentials: "include", signal });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? "We can't read the access grid right now.");
  }
  return (await res.json()) as AccessGridData;
}

/**
 * Submit a change set. Resolves to an error message, or null on success.
 *
 * Always posts the `changes` array, even for one change — one shape means the
 * mover pair is not a special case anyone can forget to use.
 */
export async function saveAccess(
  changes: Array<Change | ModuleChange | TabChange>,
): Promise<string | null> {
  const res = await fetch(`${API_BASE}/v1/entitlements`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ changes }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  // UX S8 error: the row reverts visibly with a reason — never an optimistic lie.
  return body.message ?? "Not saved — try again.";
}

/**
 * Put someone on the roster with no entitlements. Error message, or null.
 *
 * Adding and granting stay two calls because they are two decisions. The new
 * person lands with every area at `none`, and the grant is the next, separate,
 * audited act.
 */
export async function addPerson(email: string, displayName: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/v1/people`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim().toLowerCase(), display_name: displayName.trim() }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  // The API's own sentence, so a refusal reads the same in both places.
  return body.message ?? "Couldn't add that person — try again.";
}

/** Take someone off the portal entirely, revoking every area. Message, or null. */
export async function removePerson(email: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/v1/people/${encodeURIComponent(email)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  return body.message ?? "Couldn't remove that person — try again.";
}
